/**
 * Spectrum whole-cart recommendations — client fetch + render.
 *
 * Reads the current cart, posts its product identifiers (GID-first, so the endpoint's
 * cart-exclusion can match them) to the Spectrum
 * app-proxy cart-recommendations endpoint, and renders the returned products
 * into [data-spectrum-cart-recs]. The server reads the FBT envelope itself;
 * the client sends only identifiers. Re-fetches on cart change so the strip
 * tracks the cart.
 *
 * Fail-closed: empty cart, non-2xx (incl. rate-limit), or no recommendations
 * leaves the section hidden. Stale responses are ignored via a request token
 * so a slow earlier fetch can't overwrite a newer one.
 *
 * Card markup mirrors spectrum-cart-product-card's `.spectrum-cart-card`
 * classes (its stylesheet is bundled via the sibling cart strips). ATC uses the
 * shared quick-add dialog keyed by handle — the proxy returns a handle, not a
 * variant id, so single- and multi-variant products both open the dialog.
 */
(function () {
  if (window.__spectrumCartRecsInit) return;
  window.__spectrumCartRecsInit = true;

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  // Prices come back as { amount: "1299.00", currencyCode: "INR" }. Frido is INR;
  // render the rounded rupee amount to match the SSR cards (money | replace '.00').
  function money(p) {
    if (!p || p.amount == null) return '';
    var n = parseFloat(p.amount);
    if (isNaN(n)) return '';
    return '₹' + Math.round(n).toLocaleString('en-IN');
  }

  function section() {
    return document.querySelector('[data-spectrum-cart-recs]');
  }

  // One entry per distinct product in the cart (the endpoint keys on products).
  function cartProducts(cart) {
    var seen = {};
    var out = [];
    (cart.items || []).forEach(function (it) {
      var pid = it.product_id != null ? String(it.product_id) : null;
      if (!pid || seen[pid]) return;
      seen[pid] = 1;
      // Send the GID as well as the raw id. The endpoint picks ONE identifier per cart
      // product (`productGid ?? productId ?? handle`) and matches it against the cards'
      // identifiers, which are GIDs — so a bare numeric id matched nothing and the cart's
      // own products came back as recommendations. Verified live: sending only
      // `productId: "9951875334425"` returned frido-orthotics-posture-corrector while it
      // sat in the cart; adding the GID excluded it.
      out.push({
        productGid: 'gid://shopify/Product/' + pid,
        productId: pid,
        handle: it.handle,
        quantity: it.quantity || 1,
      });
    });
    return out;
  }

  function cardHtml(r) {
    var url = r.url || '/products/' + r.handle;
    var img =
      r.image && r.image.url
        ? '<img class="spectrum-cart-card__img" src="' +
          esc(r.image.url) +
          '" alt="' +
          esc(r.image.altText || r.title || '') +
          '" loading="lazy"' +
          (r.image.width ? ' width="' + r.image.width + '"' : '') +
          (r.image.height ? ' height="' + r.image.height + '"' : '') +
          '>'
        : '';
    var hasCompare =
      r.compareAtPrice &&
      r.price &&
      parseFloat(r.compareAtPrice.amount) > parseFloat(r.price.amount);
    /* The composer is the ONLY source of the MRP. There is deliberately no fallback.
       There used to be one: when the composer omitted compare-at, this took
       `compare_at_price` from /products/{handle}.js. That field is the DEAREST variant's
       compare-at, and it was rendered against `r.price` — the composer's DEFAULT-VARIANT
       price. Two different variants, one price tag, advertising a discount no variant was
       ever sold at.
       Worse, it fired precisely when the composer was RIGHT: a product whose default
       variant has no compare-at correctly returns null, and the fallback then invented an
       MRP for it. Same defect, and the same fix, as dropping the Admin backfill in
       frido-commerce-backend#39 — if the MRP is missing, the composer is where to fix it. */
    var mrpText = hasCompare ? money(r.compareAtPrice) : '';
    var mrp = mrpText
      ? '<s class="spectrum-cart-card__mrp">' + mrpText + '</s>'
      : '';
    return (
      '<li class="spectrum-cart-card">' +
      '<a class="spectrum-cart-card__media" href="' +
      esc(url) +
      '" tabindex="-1" aria-hidden="true">' +
      img +
      '</a>' +
      '<div class="spectrum-cart-card__info">' +
      '<div class="spectrum-cart-card__text">' +
      '<a class="spectrum-cart-card__title" href="' +
      esc(url) +
      '">' +
      esc(r.title) +
      '</a>' +
      '</div>' +
      '<span class="spectrum-cart-card__price">' +
      mrp +
      '<span class="spectrum-cart-card__chip"><span class="spectrum-cart-card__final">' +
      money(r.price) +
      '</span></span>' +
      '</span>' +
      '<button type="button" class="button spectrum-cart-card__add" data-spectrum-quick-add-trigger="' +
      esc(r.handle) +
      '" aria-haspopup="dialog" aria-label="Choose options for ' +
      esc(r.title) +
      '">ADD</button>' +
      '</div>' +
      '</li>'
    );
  }

  // Per-product detail the composer doesn't return: `tags`, to honour the
  // "do-not-recommend" block. Fetched from /products/{handle}.js, cached per handle.
  // Fail-open: a failed lookup returns null and the product is not blocked.
  // Deliberately does NOT read price/compare_at_price any more — see the MRP comment
  // above; those are product-level aggregates and pairing them with the composer's
  // variant price fabricated discounts.
  var productCache = {};
  function fetchProduct(handle) {
    if (productCache[handle]) return productCache[handle];
    productCache[handle] = fetch('/products/' + encodeURIComponent(handle) + '.js', {
      headers: { Accept: 'application/json' },
    })
      .then(function (r) {
        return r.ok ? r.json() : null;
      })
      .then(function (p) {
        if (!p) return null;
        var tags = (p.tags || []).map(function (t) {
          return String(t).toLowerCase();
        });
        return {
          blocked: tags.indexOf('do-not-recommend') !== -1,
        };
      })
      .catch(function () {
        return null;
      });
    return productCache[handle];
  }

  // Last successfully painted cards. The cart drawer re-renders this section on every
  // cart change, which replaces it with the server's empty, `hidden` version — so keep
  // the markup to restore instantly instead of blanking until the next fetch resolves.
  var lastCardsHtml = '';

  /**
   * Make the strip visible NOW, without waiting on the network.
   *
   * The section is server-rendered `hidden`, and nothing unhid it until /cart.js came
   * back — so on drawer open the shopper saw nothing at all, not even the shimmer, until
   * a round-trip finished. Combined with the section re-render blanking the strip on each
   * cart change, that read as the strip loading twice.
   *
   * Restores the previous cards when we have them (no flash), otherwise the shimmer.
   * Safe to call unconditionally: the snippet only renders when the cart has items.
   */
  /* Hide the strip AND forget the cards that were on it.

     Every deliberate hide means "there is nothing to show for the cart as it is
     now", so the remembered cards are stale from that moment. Keeping them let
     primeStrip — and now restoreIfBlanked — paint recommendations for an older
     cart back over a strip that had correctly gone away. */
  function hideStrip(sec) {
    if (!sec) return;
    sec.classList.remove('is-loading');
    sec.hidden = true;
    lastCardsHtml = '';
  }

  /* The morph that blanks this strip is ASYNCHRONOUS, and that is why every
     scheduling fix has missed it.

     A sections-less `cart:update` — what frido-free-gift's announce() and
     spectrum-cart-sync's refresh both dispatch — sends cart-items-component down
     its `sectionRenderer.renderSection()` branch, which is a network round trip.
     Measured on the live cart: event at t=1ms, section request at t=3ms, morph
     landing at t=750ms and patching the strip back to the server's `hidden`
     markup. scheduleRefresh's three primes (sync, timeout 0, rAF) all fire inside
     the first frame, so they are ~730ms too early, and nothing re-primes until
     the NEXT cart event. That is the blank gap, and the late shimmer after it.

     No fixed delay can win a race against a variable-latency fetch, so stop
     racing it: watch the strip, and whenever something blanks it while we still
     hold cards, put them back. */
  function restoreIfBlanked() {
    var sec = section();
    if (!sec) return;
    var list = sec.querySelector('[data-spectrum-cart-recs-list]');
    if (!list) return;
    if (list.querySelector('.spectrum-cart-card')) return; // real cards are up
    if (!lastCardsHtml) return; // deliberately hidden, or nothing painted yet
    list.innerHTML = lastCardsHtml;
    sec.classList.remove('is-loading');
    sec.hidden = false;
  }

  var stripObserver = null;
  var observedSection = null;

  /* Re-attached rather than attached once: a morph can replace the section node,
     which would silently kill an observer bound to the old one. */
  function watchStrip() {
    if (typeof MutationObserver !== 'function') return;
    var sec = section();
    if (!sec || sec === observedSection) return;
    if (stripObserver) stripObserver.disconnect();
    observedSection = sec;
    /* The restore mutates the strip and so re-enters this callback once. The
       `.spectrum-cart-card` guard above makes that second pass a no-op, so it
       settles instead of looping. */
    stripObserver = new MutationObserver(restoreIfBlanked);
    stripObserver.observe(sec, {
      attributes: true,
      attributeFilter: ['hidden', 'class'],
      childList: true,
      subtree: true
    });
  }

  function primeStrip() {
    var sec = section();
    if (!sec) return;
    var list = sec.querySelector('[data-spectrum-cart-recs-list]');
    if (!list || list.querySelector('.spectrum-cart-card')) return; // real cards already up
    if (lastCardsHtml) {
      list.innerHTML = lastCardsHtml;
      sec.classList.remove('is-loading');
      sec.hidden = false;
    } else {
      showLoading(sec, parseInt(sec.getAttribute('data-max'), 10) || 5);
    }
  }

  function paint(sec, recs) {
    var list = sec.querySelector('[data-spectrum-cart-recs-list]');
    if (!list) return;
    var max = parseInt(sec.getAttribute('data-max'), 10) || 5;
    var candidates = (recs || []).filter(function (r) {
      return r && r.handle && r.availableForSale !== false;
    });
    if (!candidates.length) {
      hideStrip(sec);
      return;
    }
    // Look up every candidate before rendering so products tagged
    // "do-not-recommend" never paint (no flash), and the struck MRP is present
    // on first render. The shimmer skeleton stays up meanwhile.
    /* Each lookup is raced against a timeout. fetchProduct() already catches rejections, so
       the only way this Promise.all fails to settle is a request that HANGS — and a hang is
       not a rejection, so no catch can rescue it. One stalled /products/x.js would then hold
       the shimmer up permanently, which is exactly the stuck strip on a first add: the
       recommendations endpoint answered fine, the per-product lookup behind it did not.
       Timing out to null just means that product is treated as un-blocked. */
    function lookup(handle) {
      return Promise.race([
        fetchProduct(handle),
        new Promise(function (resolve) {
          setTimeout(function () {
            resolve(null);
          }, 4000);
        }),
      ]);
    }

    Promise.all(
      candidates.map(function (r) {
        return lookup(r.handle);
      })
    ).catch(function () {
      return [];
    }).then(function (infos) {
      infos = infos || [];
      var allowed = [];
      candidates.forEach(function (r, i) {
        var info = infos[i];
        if (info && info.blocked) return; // honour the do-not-recommend tag
        allowed.push(r);
      });
      sec.classList.remove('is-loading');
      var html = allowed.slice(0, max).map(cardHtml).join('');
      if (!html) {
        hideStrip(sec);
        return;
      }
      list.innerHTML = html;
      sec.hidden = false;
      lastCardsHtml = html;
    });
  }

  // Shimmer skeleton tile — mirrors the real card chassis (120px, media + two
  // text lines) so the strip doesn't reflow when real cards replace it.
  var SKELETON_CARD =
    '<li class="spectrum-cart-recs__skel" aria-hidden="true">' +
    '<span class="spectrum-cart-recs__skel-media"></span>' +
    '<span class="spectrum-cart-recs__skel-line"></span>' +
    '<span class="spectrum-cart-recs__skel-line spectrum-cart-recs__skel-line--short"></span>' +
    '<span class="spectrum-cart-recs__skel-line spectrum-cart-recs__skel-line--price"></span>' +
    '<span class="spectrum-cart-recs__skel-btn"></span>' +
    '</li>';

  // Show shimmer placeholders while the app-proxy recommendations request is in
  // flight, so the shopper sees the strip loading instead of a blank gap.
  function showLoading(sec, count) {
    var list = sec.querySelector('[data-spectrum-cart-recs-list]');
    if (!list) return;
    var n = Math.max(1, Math.min(count || 4, 5));
    var html = '';
    for (var i = 0; i < n; i++) html += SKELETON_CARD;
    list.innerHTML = html;
    sec.classList.add('is-loading');
    sec.hidden = false;
  }

  var reqToken = 0;
  function refresh() {
    var sec = section();
    if (!sec) return;
    var token = ++reqToken;
    var proxy = sec.getAttribute('data-proxy') || 'spectrum_ai_frido';
    var max = parseInt(sec.getAttribute('data-max'), 10) || 5;

    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) {
        return r.json();
      })
      .then(function (cart) {
        var cp = cartProducts(cart);
        if (!cp.length) {
          var s0 = section();
          if (s0) hideStrip(s0);
          return null;
        }
        // Shimmer only on the FIRST load. On a cart-change refetch (ATC / qty /
        // remove) the strip already has cards — keep them visible and update
        // silently, so it doesn't flash back to skeletons on every change.
        var sLoad = section();
        if (sLoad && token === reqToken) {
          var listNow = sLoad.querySelector('[data-spectrum-cart-recs-list]');
          if (!listNow || !listNow.querySelector('.spectrum-cart-card')) {
            /* primeStrip, not showLoading: the cards are missing here because the section
               re-render replaced them with the server's skeletons, NOT because this is a
               first load. Shimmering again on every cart change is the second of the two
               flashes — restore the previous cards when we have them and update silently. */
            primeStrip();
          }
        }
        return fetch('/apps/' + proxy + '/cart/recommendations.json', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ cartProducts: cp, maxResults: max, schemaVersion: 1 }),
        }).then(function (res) {
          return res.ok ? res.json() : null;
        });
      })
      .then(function (data) {
        if (token !== reqToken) return; // a newer refresh superseded this one
        var s = section();
        if (s) paint(s, data && data.recommendations);
      })
      .catch(function () {
        /* An empty catch here used to swallow every failure. That was survivable while the
           section shipped hidden — a dead request simply meant no strip. Since the loading
           state is server-rendered (so a morph cannot blank it), the same dead request now
           leaves the shimmer up forever, which is what a stuck "You might love these too"
           actually is: not a slow endpoint, but a rejected fetch with nobody to clear it. */
        if (token !== reqToken) return;
        var s = section();
        if (!s) return;
        hideStrip(s);
      });

    /* Backstop: if neither branch above ever runs — the promise never settles, the tab was
       throttled mid-flight, an exception fired outside the chain — drop the shimmer rather
       than shimmering indefinitely. Cleared implicitly by the token check. */
    setTimeout(function () {
      if (token !== reqToken) return;
      var s = section();
      if (s && s.classList.contains('is-loading')) {
        hideStrip(s);
      }
    }, 12000);
  }

  /**
   * One logical cart change fans out into a burst of events, from more than one source:
   * spectrum-sdk's _dispatchCartUpdateEvents fires FIVE names (three of which we listen
   * to) on both document and window, and Horizon's own product form fires its
   * cart:update separately, at a different moment in the same add.
   *
   * Bound directly, that meant one add cost three /cart.js fetches and three proxy POSTs,
   * and — because the dispatches are spread across ticks rather than one synchronous
   * burst — the strip visibly repainted two or three times before settling. The
   * reqToken guard inside refresh() only suppresses a STALE paint; it cannot merge
   * separately-timed cycles.
   *
   * Trailing debounce: the timer restarts on each event, so the whole burst collapses
   * into a single fetch and a single paint once the cart stops changing. A dispatch
   * arriving later than the window still starts a fresh cycle — indistinguishable from a
   * genuine second cart change from here.
   */
  var REFRESH_DEBOUNCE_MS = 150;
  /* Ceiling on the debounce. A pure trailing debounce restarts its timer on every event,
     so a stream of cart events spaced closer than the window STARVES it and refresh()
     never runs at all — the strip then shimmers until something else (a navigation, which
     takes the DOMContentLoaded path) kicks it. That is the stuck strip after a PDP add.
     With a max wait the burst still collapses to one fetch, but the fetch is guaranteed
     to happen within 600ms of the FIRST event no matter how long the stream continues. */
  var REFRESH_MAX_WAIT_MS = 600;
  var refreshTimer = null;
  var burstStartedAt = 0;
  function scheduleRefresh() {
    // Twice: once now, and once after the drawer's renderContents has swapped in the
    // fresh (empty, hidden) section — whichever order those happen in, one of the two
    // lands on the new DOM.
    /* Three times on purpose. The section re-render lands SYNCHRONOUSLY after the cart
       event, so a prime that runs before it is immediately undone — that is the first of
       the two flashes. The timeout re-primes once the current task (morph included) has
       finished; the rAF covers a morph that lands a frame later. Each call is a no-op once
       real cards are up. */
    primeStrip();
    setTimeout(primeStrip, 0);
    if (typeof requestAnimationFrame === 'function') requestAnimationFrame(primeStrip);
    /* Cheap, and the section node may have been swapped by the last morph. */
    watchStrip();
    var now = Date.now();
    if (!refreshTimer) burstStartedAt = now;
    else clearTimeout(refreshTimer);

    var elapsed = now - burstStartedAt;
    var wait = REFRESH_DEBOUNCE_MS;
    if (elapsed + wait > REFRESH_MAX_WAIT_MS) {
      wait = Math.max(0, REFRESH_MAX_WAIT_MS - elapsed);
    }

    refreshTimer = setTimeout(function () {
      refreshTimer = null;
      burstStartedAt = 0;
      refresh();
    }, wait);
  }

  document.addEventListener('cart:update', scheduleRefresh);
  document.addEventListener('cart:updated', scheduleRefresh);
  document.addEventListener('cart:refresh', scheduleRefresh);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      watchStrip();
      primeStrip();
      refresh();
    });
  } else {
    watchStrip();
    primeStrip();
    refresh();
  }
})();
